import React, { useEffect, useState } from "react";
import { sendPOSTRequest } from "../../requestHelper/sendRequest";
import { useTranslation } from "react-i18next";
import { FrontendUserInfo } from "node-library/dist/portal";
import {
	FrontendContentPage,
	Severity,
	UserType,
} from "node-library/dist/constants";
import { CustomIconButton } from "../../common-components/button";
import LastPageIcon from "@mui/icons-material/LastPage";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

import {
	NeutralColor,
	PrimaryColor,
	SecondaryColor,
} from "node-library/dist/colors";
import { Divider, Tooltip } from "@mui/material";
import SidebarRow from "./sidebarRow";
import { checkUserTypeMeetsRequirement } from "../../helpers/userHelper";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	SidebarInfoIcon,
	SidebarOrderIcon,
	SidebarProfileIcon,
	SidebarRosterIcon,
	SidebarOrganizationIcon,
	SidebarSignoutIcon,
} from "../../common-components/sidebarSvgIcons";

import MenuIcon from "@mui/icons-material/Menu";

interface Props {
	user: FrontendUserInfo | undefined;
	contentPage: FrontendContentPage;
	unsavedChanges: boolean;
	isExpanded: boolean;
	setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	navigateToContentPage: (page: FrontendContentPage) => void;
	setUnsavedChangesModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
	setProceedAction: React.Dispatch<React.SetStateAction<() => void>>;
}

export default function DesktopSidebar(props: Props) {
	const { t: translation } = useTranslation();
	const { isExpanded, setIsExpanded } = props;

	const { openSnackbar } = useSnackbar();

	const [isMobile, setIsMobile] = useState(false);
	const [mobileAvatarTooltipOpen, setMobileAvatarTooltipOpen] = useState(false);

	// Detect screen width and update `isMobile` state
	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 600); // 600px breakpoint for mobile
		};

		handleResize(); // Check initial screen size
		window.addEventListener("resize", handleResize); // Add resize event listener

		return () => window.removeEventListener("resize", handleResize); // Cleanup event listener
	}, []);

	const user = props.user;

	function gotoPage(page: FrontendContentPage) {
		if (props.contentPage === page) {
			return;
		}
		if (!props.unsavedChanges) {
			props.navigateToContentPage(page);
		} else {
			props.setProceedAction(() => () => {
				props.navigateToContentPage(page);
			});
			props.setUnsavedChangesModalOpen(true);
		}
	}
	async function signOut() {
		try {
			const response = await sendPOSTRequest("logins/signout", {});
			localStorage.removeItem("billionspectra_userInfo");
			localStorage.removeItem("billionspectra_companyInfo");
		} catch (err) {
			console.error(`Unable to sign out: ${err}`);
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			window.location.href = "/login";
		}
	}

	const actionRowElements = [
		...(isMobile
			? [
					{
						sectionLabel: "",
						children: [
							{
								label: translation("NewOrder"),
								icon: (
									<AddRoundedIcon
										sx={{
											color:
												props.contentPage === FrontendContentPage.NEW_ORDER_PAGE
													? "white"
													: NeutralColor.NEUTRAL_GREY_400,
										}}
									></AddRoundedIcon>
								),
								page: FrontendContentPage.NEW_ORDER_PAGE,
								minimumRole: UserType.ADMIN,
							},
						],
					},
			  ]
			: []),
		{
			sectionLabel: translation("Menu"),
			children: [
				{
					label: translation("Orders"),
					icon: (
						<SidebarOrderIcon
							strokeColor={
								props.contentPage === FrontendContentPage.VIEW_ORDER_PAGE
									? "white"
									: NeutralColor.NEUTRAL_GREY_400
							}
						></SidebarOrderIcon>
					),
					page: FrontendContentPage.VIEW_ORDER_PAGE,
					minimumRole: UserType.ENGINEER,
					openToAll: true,
				},
				{
					label: translation("Roster"),
					icon: (
						<SidebarRosterIcon
							strokeColor={
								props.contentPage === FrontendContentPage.ROSTER_PAGE
									? "white"
									: NeutralColor.NEUTRAL_GREY_400
							}
						></SidebarRosterIcon>
					),

					page: FrontendContentPage.ROSTER_PAGE,
					minimumRole: UserType.ADMIN,
				},
				{
					label: translation("Organization"),
					icon: (
						<SidebarOrganizationIcon
							strokeColor={
								props.contentPage === FrontendContentPage.ORGANIZATION_PAGE
									? "white"
									: NeutralColor.NEUTRAL_GREY_400
							}
						></SidebarOrganizationIcon>
					),

					page: FrontendContentPage.ORGANIZATION_PAGE,
					minimumRole: UserType.ADMIN,
				},
			],
		},
		{
			sectionLabel: translation("Account"),
			children: [
				{
					label: translation("Profile"),
					icon: (
						<SidebarProfileIcon
							strokeColor={
								props.contentPage === FrontendContentPage.USER_INFO_PAGE
									? "white"
									: NeutralColor.NEUTRAL_GREY_400
							}
						></SidebarProfileIcon>
					),

					page: FrontendContentPage.USER_INFO_PAGE,
					minimumRole: UserType.ENGINEER,
					openToAll: true,
				},
			],
		},
	];
	const footerRowElements = [
		{
			label: translation("HelpAndContact"),
			icon: (
				<SidebarInfoIcon
					strokeColor={
						props.contentPage === FrontendContentPage.HELP_PAGE
							? "white"
							: NeutralColor.NEUTRAL_GREY_400
					}
				></SidebarInfoIcon>
			),
			minimumRole: UserType.CLIENT,
			openToAll: true,
			page: FrontendContentPage.HELP_PAGE,

			onClick: () => gotoPage(FrontendContentPage.HELP_PAGE),
		},
		{
			label: translation("Logout"),
			icon: <SidebarSignoutIcon></SidebarSignoutIcon>,
			minimumRole: UserType.CLIENT,
			openToAll: true,
			onClick: signOut,
		},
	];

	const avatarPlaceholder: React.ReactNode = (
		<div
			className={`typography-body-01`}
			style={{ color: "white" }}
		>{`${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`}</div>
	);
	return (
		<div
			className={`landing-page-sidebar ${
				isExpanded ? `` : `sidebar-collapsed`
			}`}
		>
			<div className="sidebar-header">
				<div
					className={` ${
						isExpanded || isMobile
							? `ppb-logo sidebar-ppb-logo`
							: `sidebar-collapse-hide`
					}`}
				></div>
				<div style={{ display: "flex", gap: "5px" }}>
					{isMobile ? (
						<>
							<Tooltip
								PopperProps={{
									sx: {
										"& .MuiTooltip-tooltip": {
											backgroundColor: NeutralColor.NEUTRAL_GREY_900,
											padding: "12px 16px",
										},
									},
								}}
								title={`${user?.firstName} ${user?.lastName}`}
								arrow
								open={mobileAvatarTooltipOpen} // Control the tooltip's visibility with state
								onClose={() => setMobileAvatarTooltipOpen(false)} // Close tooltip when clicking outside
							>
								<div
									className="sidebar-user-avatar"
									onClick={() => {
										setMobileAvatarTooltipOpen(!mobileAvatarTooltipOpen);
									}}
								>
									{avatarPlaceholder}
								</div>
							</Tooltip>

							<CustomIconButton
								id={"sidebar-expand-toggle-button"}
								icon={<MenuIcon />}
								onClick={() => {
									setIsExpanded(!isExpanded);
								}}
								buttonClassName="sidebar-expand-toggle-button"
							/>
						</>
					) : (
						<CustomIconButton
							id={"sidebar-expand-toggle-button"}
							icon={
								isExpanded ? <FirstPageIcon></FirstPageIcon> : <LastPageIcon />
							}
							onClick={() => {
								setIsExpanded(!isExpanded);
							}}
							buttonClassName="sidebar-expand-toggle-button"
						/>
					)}
				</div>
			</div>

			{!isMobile && (
				<div
					className={`sidebar-user-info ${
						isExpanded ? `` : `sidebar-user-info-collapsed`
					}`}
				>
					<div className="sidebar-user-avatar">{avatarPlaceholder}</div>
					<div className="sidebar-username-email-container">
						<div
							className={`typography-heading-04 typography-bold  ${
								isExpanded ? `` : `sidebar-collapse-hide`
							}`}
							style={{ wordWrap: "break-word" }}
						>{`${user?.firstName} ${user?.lastName}`}</div>
						<div
							className={`typography-caption grey-text ${
								isExpanded ? `` : `sidebar-collapse-hide`
							}`}
							style={{ wordWrap: "break-word" }}
						>{`${user?.userName}`}</div>
					</div>
				</div>
			)}
			{!isMobile && (
				<Divider
					className={`sidebar-section-divider ${
						isExpanded ? "" : "sidebar-section-divider-collapsed"
					}`}
					orientation="horizontal"
					flexItem
				/>
			)}
			{!isMobile &&
				user &&
				checkUserTypeMeetsRequirement(user.role, UserType.ADMIN) && (
					<div
						className={`new-order-button ${
							props.contentPage === FrontendContentPage.NEW_ORDER_PAGE
								? `new-order-button-selected`
								: ``
						}`}
						onClick={() => {
							gotoPage(FrontendContentPage.NEW_ORDER_PAGE);
						}}
					>
						<AddRoundedIcon
							sx={{ display: isExpanded ? `none` : "", strokeWidth: "1.5px" }}
						/>
						<span
							className={`typography-body-01  ${
								isExpanded ? `` : `sidebar-collapse-hide`
							}`}
						>
							{translation("CreateOrder")}
						</span>
					</div>
				)}

			{actionRowElements.map((element, actionRowIndex) => {
				const rowItems: Array<React.ReactNode> = element.children.map(
					(childRow, index) => {
						if (
							user &&
							checkUserTypeMeetsRequirement(
								user.role,
								childRow.minimumRole,
								childRow.openToAll
							)
						) {
							return (
								<SidebarRow
									key={`sidebar-row-item-${childRow.label}`}
									label={childRow.label}
									icon={childRow.icon}
									isExpanded={isExpanded}
									isMobile={isMobile}
									selected={props.contentPage === childRow.page}
									onClick={() => {
										gotoPage(childRow.page);
									}}
								></SidebarRow>
							);
						} else {
							return null;
						}
					}
				);
				return isMobile && !props.isExpanded ? null : (
					<div key={`sidebar-action-row-elements-${element.sectionLabel}`}>
						{/* <Divider
							className="sidebar-section-divider"
							orientation="horizontal"
							sx={{ my: 1 }}
							flexItem
						/> */}

						<div
							className={`typography-caption sidebar-section-label ${
								isExpanded ? `` : `sidebar-collapse-hide`
							}`}
							style={{
								color: NeutralColor.NEUTRAL_GREY_500,
							}}
						>
							{element.sectionLabel}
						</div>
						<div className="sidebar-action-row-container">{rowItems}</div>
					</div>
				);
			})}

			{(!isMobile || isExpanded) && (
				<div className="sidebar-footer">
					<Divider
						orientation="horizontal"
						flexItem
						className={`sidebar-section-divider ${
							isExpanded ? "" : "sidebar-section-divider-collapsed"
						}`}
					/>
					{footerRowElements.map((childRow, index) => {
						if (
							user &&
							checkUserTypeMeetsRequirement(
								user.role,
								childRow.minimumRole,
								childRow.openToAll
							)
						) {
							return (
								<SidebarRow
									key={`sidebar-footer-element-${childRow.label}`}
									label={childRow.label}
									icon={childRow.icon}
									isExpanded={isExpanded}
									selected={
										childRow.page ? props.contentPage === childRow.page : false
									}
									isMobile={isMobile}
									onClick={childRow.onClick}
								></SidebarRow>
							);
						} else {
							return null;
						}
					})}
				</div>
			)}
		</div>
	);
}
